import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams, PAGINATION_SEARCH_FIELDS } from "../http-params.util";

/**
 * InventoryApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/inventoryApi/inventoryApi.service.js.
 *
 * Inventory items are stored under the `addons` endpoint.
 */
const ADDONS_BASE_V2 = "/v2/api/addons";

const INVENTORY_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "saleChannel",
  "addonTypes",
] as const;

interface InventoryItemPayload {
  id?: number | string;
  sku: string;
  name: string;
  type: string;
  order: unknown;
  price: unknown;
  basePrice: unknown;
  active: boolean;
  quantity: number;
  printTicket: boolean;
  salesChannels: unknown;
  description?: string;
  resourceId?: number | string;
}

@Injectable({ providedIn: "root" })
export class InventoryApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(ADDONS_BASE_V2, {
        params: buildParams(props, INVENTORY_LIST_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${ADDONS_BASE_V2}/${id}`));
  }

  create(props: InventoryItemPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(ADDONS_BASE_V2, {
        sku: props.sku,
        name: props.name,
        type: props.type,
        order: props.order,
        price: props.price,
        basePrice: props.basePrice,
        active: props.active,
        quantity: props.quantity,
        printTicket: props.printTicket,
        salesChannels: props.salesChannels,
        description: props.description,
      }),
    );
  }

  update(props: InventoryItemPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(ADDONS_BASE_V2, {
        id: props.id,
        sku: props.sku,
        name: props.name,
        type: props.type,
        order: props.order,
        price: props.price,
        basePrice: props.basePrice,
        active: props.active,
        quantity: props.quantity,
        printTicket: props.printTicket,
        resourceId: props.resourceId,
        salesChannels: props.salesChannels,
        description: props.description,
      }),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${ADDONS_BASE_V2}/${id}`));
  }
}
